import { z } from 'zod'

import { api } from '#admin/lib/api-client.ts'

/**
 * The exchange rates of this edition. A new draft invoice in a different currency gets a copy of
 * the rate. A later change does not change that invoice.
 */
const exchangeRateSchema = z.object({
  currency: z.string(),
  paymentCurrency: z.string(),
  /** `1 <currency> = <rate> <paymentCurrency>`, as a decimal string. */
  rate: z.string(),
  updatedAt: z.string(),
})

export type ExchangeRate = z.infer<typeof exchangeRateSchema>

const base = '/v1/billing/admin/exchange-rates'

export function fetchExchangeRates(signal?: AbortSignal) {
  return api.get(base, {
    schema: z.object({ paymentCurrency: z.string(), items: z.array(exchangeRateSchema) }),
    ...(signal ? { signal } : {}),
  })
}

export function setExchangeRate({ currency, rate }: { currency: string; rate: string }) {
  return api.put(`${base}/${currency}`, { schema: exchangeRateSchema, body: { rate } })
}

export function removeExchangeRate(currency: string) {
  return api.delete(`${base}/${currency}`, { schema: z.undefined() })
}

/** `numeric(20, 8)`, more than 0. The API checks it too. */
export const RATE_PATTERN = /^\d{1,12}(?:\.\d{1,8})?$/

export const isRate = (value: string) => RATE_PATTERN.test(value) && Number(value) > 0

/** `1 USD = 17,015 IDR`. All the decimals stay, so the operator sees the exact rate. */
export const rateText = (currency: string, rate: string, paymentCurrency: string) =>
  `1 ${currency} = ${Number(rate).toLocaleString('en-US', { maximumFractionDigits: 8 })} ${paymentCurrency}`
